import { useEffect, useRef } from "react";
import type { MouseEvent } from "react";
import { NavLink } from "react-router";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import AltArrowRightLinear from "~icons/solar/alt-arrow-right-linear";
import AiLine from "~icons/mingcute/ai-line";
import dahlingLogoWhite from "../../assets/dahling_logo_white.svg";
import { Icon } from "../ui/Icon";
import { cn } from "../../lib/utils";
import { acquireScrollLock, releaseScrollLock } from "../../lib/scroll-lock";
import { useSidebarNav } from "./useSidebarNav";
import { usePermissions } from "../../hooks/usePermissions";
import { Permissions } from "../../types/permissions";

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function MobileSidebar({ open, onClose }: MobileSidebarProps) {
  const { navItems, handleNewChat } = useSidebarNav();
  const { hasPermission } = usePermissions();
  const canChat = hasPermission(Permissions.AI_CHAT);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus management: move focus to close button when open, restore on close
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      // Small delay to wait for the transition to start
      requestAnimationFrame(() => closeButtonRef.current?.focus());
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      acquireScrollLock();
      return () => releaseScrollLock();
    }
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Sidebar panel */}
      <aside
        role="dialog"
        aria-modal="true"
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[272px] flex-col gap-5 overscroll-contain bg-sidebar-bg px-5 pb-4 transition-transform duration-300 lg:hidden",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        aria-label="Mobile navigation"
      >
        {/* Header */}
        <div className="flex h-[83px] items-center justify-between">
          <img src={dahlingLogoWhite} alt="Dahling" width={135} height={32} />
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label="Close navigation"
          >
            <Icon icon={CloseCircleLinear} size="lg" />
          </button>
        </div>

        {/* Nav items */}
        <nav>
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.label}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    if (item.onClick) {
                      item.onClick(e);
                    }
                    onClose();
                  }}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-[15px] rounded-full p-3 text-body-md transition-colors",
                      isActive
                        ? "sidebar-item-active font-bold text-white"
                        : "font-normal text-text-placeholder hover:text-white"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon icon={item.icon} size="lg" />
                      <span className="flex-1">{item.label}</span>
                      {isActive && (
                        <Icon icon={AltArrowRightLinear} size="md" />
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Meet dahling AI CTA */}
        {canChat && (
        <div className="mt-auto">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-secondary p-3 shadow-[0px_10px_30px_0px_rgba(0,0,0,0.35)]">
            <h3 className="text-body-lg font-bold text-white">
              Meet dahling AI
            </h3>
            <p className="text-center text-body-sm text-white/70">
              Unlock powerful AI to automate your workflow—from prepping
              interview questions to summarizing key insights.
            </p>
            <button
              type="button"
              onClick={(e) => {
                handleNewChat(e);
                onClose();
              }}
              className="flex w-full items-center justify-center gap-1 rounded-full bg-gradient-to-r from-gradient-start to-gradient-end px-4 py-2.5 text-body-md font-bold text-white"
            >
              <Icon icon={AiLine} size="md" />
              Chat with AI now
            </button>
          </div>
        </div>
        )}
      </aside>
    </>
  );
}
